const projects = [
  {
    id: 1,
    slug: "1",
    title: "Adversarial NLP Stress-Testing for Fact Verification",
    description: "A research study stress-testing fact-checking AI models by designing misleading statements crafted to appear plausible, analyzing what makes these models fail. Extracted 500 candidate claims from ~80,000 FEVER entries using a custom complexity scoring system, then built a final dataset of 2,000 claims with 552 hand-crafted counterfactuals evaluated across 7 models on AWS EC2.",
    technologies: ["Python", "GTK4", "AWS EC2", "NLP", "LLM Inference", "FEVER Dataset"],
    github: null,
    demo: null,
    image: null,
    featured: true,
    subtitle: "NLP Research Project (CS 521) · Team of 4",
    date: "Jan 2026 – May 2026",
    impact: "Uncovered 64–96% adversarial failure rates across 7 fact-checking models on AWS EC2 (NVIDIA L40S)",
    highlights: [
      "Extracted 500 candidate claims from ~80,000 FEVER entries using a custom complexity scoring system to filter for the hardest, most ambiguous claims suited for counterfactual creation",
      "Developed a Python GTK4 annotation tool with live local LLM inference to verify model failure in real time, ensuring each example actually fooled an LLM before being included in the final dataset",
      "Created a final dataset of 2,000 claims with 552 hand-crafted counterfactuals and evaluated 7 fact-checking models on AWS EC2 (NVIDIA L40S)",
      "Uncovered 64–96% adversarial failure rates and universal failure on 34.4% of attacks across all models, even when provided the correct supporting evidence"
    ]
  },
  {
    id: 2,
    slug: "2",
    title: "Distributed Object Store with Erasure Coding and Integrity Verification",
    description: "A distributed storage system where files are stored in fragments across multiple nodes, with fault tolerance and fingerprinting that guarantee file retrieval even with node failures or data corruption. Deployed as 6 Docker containers with a FastAPI coordinator, React frontend, and CLI tool. Developed solo.",
    technologies: ["Python", "FastAPI", "Reed-Solomon Erasure Coding", "Docker", "React", "SHA-256"],
    github: "https://github.com/karan1505/cs588-object-store",
    demo: null,
    image: null,
    featured: true,
    subtitle: "Security & Privacy Course Project (CS 588) · Solo",
    date: "Jan 2026 – May 2026",
    impact: "File retrieval guaranteed even with 40% node failure; 44% storage overhead reduction over naive replication",
    highlights: [
      "Deployed the system using 6 Docker containers and a FastAPI coordinator, with a React frontend and a CLI tool for testing and evaluation",
      "Implemented Reed-Solomon erasure coding in Python to achieve fault tolerance — even with 40% of nodes failing, file retrieval remains possible, reducing storage overhead by 44% over naive replication",
      "Implemented homomorphic fingerprinting over GF(256) so each storage node could detect its own silent data corruption; if the fingerprint does not match, the coordinator skips the corrupt node and reconstructs from the remaining valid fragments",
      "Verified end-to-end: after corrupting one fragment by flipping a bit and killing two fragments by terminating the nodes, the system reconstructed the original file from 3 remaining fragments, confirmed by SHA-256 match"
    ]
  },
  {
    id: 3,
    slug: "3",
    title: "Medical Chatbot Strategy Comparison System",
    description: "A comparison platform evaluating 4 different LLM strategies for medical Q&A to understand which approaches perform best and under what conditions. Compared simple LLM, multi-turn LLM, simple LLM with RAG, and multi-turn RAG across 28,000+ indexed PubMed abstracts using Llama 3.3 70B via Groq and ChromaDB. Team of 5.",
    technologies: ["LangChain", "FastAPI", "Llama 3.3 70B", "Groq", "ChromaDB", "Python", "RAG"],
    github: null,
    demo: null,
    image: null,
    featured: true,
    subtitle: "Advanced Software Engineering Project (CS 540) · Team of 5",
    date: "Jan 2026 – May 2026",
    impact: "RAG + memory eliminated hallucinations entirely; simple strategy delivered 3.5× better cost-performance",
    highlights: [
      "Compared four strategies: simple LLM, multi-turn LLM, simple LLM with RAG, and multi-turn RAG, with 28,000+ indexed abstracts from the PubMed dataset using Llama 3.3 70B via Groq and ChromaDB",
      "Implemented the baseline simple LLM strategy using FastAPI and LangChain, which all other strategies were benchmarked against",
      "Built an automated evaluation script that ran 600 benchmark sessions across all 4 strategies, measuring hallucination rate, semantic similarity, and context recall",
      "Included a hedge pre-filter that improved misclassification rate by not factoring in cases where the model accurately reports that it lacks relevant information",
      "Concluded that adding RAG and memory eliminated hallucinations entirely, but the simplest strategy delivered 3.5× better cost-performance at nearly identical answer quality"
    ]
  },
  {
    id: 4,
    slug: "4",
    title: "Academic Research Paper Search Tool with RAG on AWS",
    description: "A research document search tool powered by LLMs that automatically re-indexes when documents change, enabling natural language search across 600+ academic software engineering papers. Built entirely in Scala on AWS using a three-stage cloud pipeline combining batch ingestion, incremental indexing, and stream processing. Developed solo.",
    technologies: ["Scala", "Apache Spark", "Apache Flink", "Hadoop MapReduce", "AWS EMR", "AWS EKS", "Ollama", "Neo4j", "Docker", "Kubernetes"],
    github: null,
    demo: null,
    image: null,
    featured: true,
    subtitle: "Cloud Computing Course Project (CS 441) · Solo",
    date: "Sep 2025 – Dec 2025",
    impact: "~200ms query latency; ~90% re-indexing compute reduction; 5,000+ concept nodes extracted into Neo4j",
    highlights: [
      "Built in Scala on AWS using a three-stage cloud data pipeline ingesting 600+ academic papers, supporting natural language search, automatic delta re-indexing, and concept relationship mapping",
      "Designed the search engine using a Lucene HNSW vector index and Ollama embeddings, achieving ~200ms end-to-end query latency via a REST API, replacing the need for paid vector database services like Pinecone",
      "Implemented a Spark-based delta indexer on AWS EMR using SHA-256 content hashing so only new or modified documents are re-embedded on each run, reducing re-indexing compute by ~90%",
      "Deployed an Apache Flink streaming pipeline on AWS EKS to extract 5,000+ concept nodes and co-occurrence relationships into Neo4j, with an LLM classifying the relationship type between each concept pair",
      "Built the entire pipeline from scratch with no external RAG frameworks — no LangChain, LlamaIndex, or vector database SDK — every component implemented in Scala",
      "Unified batch ingestion (MapReduce), incremental indexing (Spark), and stream processing (Flink) into one Scala codebase deployable locally or on AWS with a single build flag, containerized via Docker on Kubernetes"
    ]
  },
  {
    id: 5,
    slug: "5",
    title: "Selective Language Modeling for Efficient LLM Training",
    description: "Replicated key findings from a NeurIPS 2024 paper by fine-tuning TinyLlama-1.1B with LoRA on a single GPU, achieving baseline perplexity using only 50% of training tokens. Implemented and compared three token selection strategies — random, stochastic top-k, and deterministic top-k — to study compute-quality tradeoffs in LLM training.",
    technologies: ["PyTorch", "LoRA", "TinyLlama", "HuggingFace", "Python"],
    github: null,
    demo: null,
    image: null,
    featured: false,
    subtitle: "Research Project (CS 512 – Advanced Machine Learning)",
    date: "Sep 2025 – Dec 2025",
    impact: "Achieved baseline perplexity using only 50% of training tokens, replicating NeurIPS 2024 findings on a single GPU",
    highlights: [
      "Replicated key findings from a NeurIPS 2024 paper by fine-tuning TinyLlama-1.1B with LoRA on a single GPU, achieving baseline perplexity using only 50% of training tokens",
      "Implemented and compared three token selection strategies — random, stochastic top-k, and deterministic top-k — finding that deterministic selection achieved faster convergence but at the cost of training stability",
      "Tracked gradient similarity and token difficulty patterns across training runs to understand which tokens contributed most to learning and which were redundant across all three strategies"
    ]
  },
  {
    id: 6,
    slug: "6",
    title: "Quizzify: Full-Stack Music Quiz Platform",
    description: "A full-stack music quiz platform where users log in with Spotify, listen to song previews, and identify tracks from album art. Features 3 difficulty levels, timed rounds, a live scoreboard, and 9 curated quiz modes. Deployed at quizzify.space as 3 independent cloud services. Team of 2.",
    technologies: ["ReactJS", "FastAPI", "Spotify OAuth 2.0", "Playwright", "MongoDB", "Docker", "Google Cloud Run", "Node.js"],
    github: "https://github.com/karan1505/spotify-quiz-app",
    demo: null,
    image: null,
    featured: true,
    subtitle: "Team of 2 · Deployed at quizzify.space",
    date: "Aug 2024 – Dec 2024",
    impact: "Production deployment with persistent scoreboards and user-generated quiz pipeline via any Spotify playlist",
    highlights: [
      "Created 3 difficulty levels, timed rounds, a live scoreboard, and 9 curated quiz modes with a custom mode for user-generated quizzes",
      "Designed the frontend using ReactJS with live audio playback; FastAPI handles the backend with 12+ REST endpoints covering Spotify OAuth, playlist fetching, quiz generation, answer validation, and score persistence",
      "Built a Playwright-based web scraper to source 30-second song preview URLs from Apple Music — necessary because Spotify's API removed audio previews during project development",
      "Deployed as 3 independent cloud services: React frontend and FastAPI backend on Render, and the Apple Music scraper on Google Cloud Run, with MongoDB Atlas for data persistence",
      "Shipped a production website with persistent scoreboards and a user-generated quiz pipeline where any Spotify playlist is automatically enriched with preview URLs on demand"
    ]
  },
  {
    id: 7,
    slug: "7",
    title: "Interactive Handwritten Character Recognition System",
    description: "Trained and compared Logistic Regression, RNN, and CNN architectures on the EMNIST dataset for handwritten character recognition, measuring accuracy and complexity tradeoffs. Built a Python inference backend with a consistent preprocessing pipeline and a React drawing interface for live predictions.",
    technologies: ["PyTorch", "TensorFlow", "scikit-learn", "React", "Python"],
    github: null,
    demo: null,
    image: null,
    featured: false,
    subtitle: "Machine Learning Course Project (CS 412)",
    date: "Jan 2025 – Apr 2025",
    impact: "End-to-end interactive demo from model training through live character prediction in browser",
    highlights: [
      "Trained and compared Logistic Regression, RNN, and CNN architectures on the EMNIST dataset for handwritten character recognition, measuring accuracy and complexity tradeoffs across all three",
      "Built a Python inference backend with a consistent preprocessing pipeline and a React drawing interface where users draw characters and see live model predictions",
      "Deployed the full pipeline end-to-end, from model training through a working interactive demo"
    ]
  },
  {
    id: 8,
    slug: "8",
    title: "Dataset Shift Analysis & Implementation",
    description: "Implemented three types of dataset shift — covariate, prior probability, and concept shift — using scikit-learn on real-world datasets to study how models fail when training and test distributions diverge. Simulated covariate shift via weather condition splits and measured prior probability shift across class imbalance ratios from 50% to 90%.",
    technologies: ["Python", "scikit-learn", "Pandas", "NumPy"],
    github: null,
    demo: null,
    image: null,
    featured: false,
    subtitle: "Responsible AI Engineering Course Project (CS 594)",
    date: "Jan 2025 – Apr 2025",
    impact: "Demonstrated practical failure modes for covariate, prior probability, and concept shift on real-world datasets",
    highlights: [
      "Implemented three types of dataset shift: covariate, prior probability, and concept shift using scikit-learn on real-world datasets to study how models fail when training and test distributions diverge",
      "Simulated covariate shift by training on sunny weather conditions and testing on rainy data, showing how feature distribution changes degrade Random Forest performance in practice",
      "Measured prior probability shift by varying class imbalance ratios from 50% to 90% majority class, and visualized concept shift breakdown using confusion matrices and ROC curves"
    ]
  }
];

export default projects;
